import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Users, FileText, Shield, Ban, Check, Eye, ExternalLink,
  Globe, MapPin, MousePointer, Share2, QrCode, Cpu, 
  HardDrive, Activity, TrendingUp, Server, Music,
  BadgeCheck, X, Crown, ChevronDown, UserCog, Sliders, 
  Save, BarChart3, PieChart, Calendar, Clock, Zap, Link2, Trash2, Search,
  MessageCircle, AlertCircle, CheckCircle, Send, User, Mail, Edit, ArrowLeft
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { motion, AnimatePresence } from "framer-motion";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart as RechartsPie, Pie, Cell, BarChart, Bar
} from "recharts";
import Sidebar from "@/components/Sidebar";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { countryFlag, localizeCountry } from "@/utils/geo";
import { getMainDomainUrl } from "@/utils/subdomain";

// Role configuration - labels are translation keys
const ROLE_CONFIG = {
  owner: { labelKey: "roleOwner", color: "bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-yellow-400 border-yellow-500/30", icon: Crown },
  admin: { labelKey: "roleAdmin", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Shield },
  moderator: { labelKey: "roleModerator", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: UserCog },
  user: { labelKey: "roleUser", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: Users }
};

const PLAN_CONFIG = {
  free: { label: "Free", color: "bg-muted text-muted-foreground" },
  pro: { label: "Pro", color: "bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-purple-300" }
};

const TICKET_STATUS_CONFIG = {
  open: { labelKey: "ticketOpen", color: "bg-blue-500", icon: AlertCircle },
  in_progress: { labelKey: "ticketInProgress", color: "bg-yellow-500", icon: Clock },
  resolved: { labelKey: "ticketResolved", color: "bg-green-500", icon: CheckCircle },
  closed: { labelKey: "ticketClosed", color: "bg-zinc-500", icon: CheckCircle }
};

const COLORS = ['#d946ef', '#8b5cf6', '#3b82f6', '#22c55e', '#eab308', '#ef4444'];

export default function AdminPanel() {
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const getPublicPageUrl = (slug) => getMainDomainUrl(`/${slug}`);
  const [users, setUsers] = useState([]);
  const [pages, setPages] = useState([]);
  const [verificationRequests, setVerificationRequests] = useState([]);
  const [globalAnalytics, setGlobalAnalytics] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);
  const [planConfigs, setPlanConfigs] = useState([]);
  const [subdomains, setSubdomains] = useState([]);
  const [subdomainsTotal, setSubdomainsTotal] = useState(0);
  const [subdomainSearch, setSubdomainSearch] = useState("");
  const [tickets, setTickets] = useState([]);
  const [ticketsTotal, setTicketsTotal] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState("");
  const [ticketStatusFilter, setTicketStatusFilter] = useState("");
  const [waitlistEmails, setWaitlistEmails] = useState([]);
  const [waitlistTotal, setWaitlistTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ["analytics", "users", "plans", "pages", "domains", "tickets", "verification", "system", "emails"];
  const initialTab = VALID_TABS.includes(searchParams.get("section")) ? searchParams.get("section") : "analytics";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Keep the active tab in the URL (?section=users) so F5 stays on the same tab
  const handleTabChange = (val) => {
    setActiveTab(val);
    const next = new URLSearchParams(searchParams);
    next.set("section", val);
    setSearchParams(next, { replace: true });
  };

  // Sync from URL when it changes externally (back/forward navigation)
  useEffect(() => {
    const section = searchParams.get("section");
    if (section && VALID_TABS.includes(section) && section !== activeTab) {
      setActiveTab(section);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  
  // User Profile Modal State
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [selectedUserPages, setSelectedUserPages] = useState([]);
  const [loadingUserProfile, setLoadingUserProfile] = useState(false);
  const [userProfileModalOpen, setUserProfileModalOpen] = useState(false);

  // Custom confirm dialog + user actions modal state
  const [confirmDialog, setConfirmDialog] = useState({ open: false });
  const [userActions, setUserActions] = useState(null); // { user } for ban/delete modal

  const askConfirm = ({ title, description, confirmText, destructive, onConfirm }) => {
    setConfirmDialog({
      open: true,
      title,
      description,
      confirmText,
      destructive,
      onConfirm: async () => {
        try { await onConfirm(); } finally { setConfirmDialog({ open: false }); }
      },
    });
  };

  const isOwner = currentUser?.role === "owner";
  const isAdmin = currentUser?.role === "admin" || isOwner;
  const canViewVPS = isOwner || isAdmin;

  // Helper to extract error message from API response
  const getErrorMessage = (error, defaultMsg) => {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || String(d)).join(', ');
    if (detail && typeof detail === 'object' && detail.msg) return detail.msg;
    return defaultMsg || t('errors', 'generic');
  };

  useEffect(() => {
    fetchData();
    fetchGlobalAnalytics();
    if (canViewVPS) fetchSystemMetrics();
    fetchVerificationRequests();
    fetchPlanConfigs();
    fetchSubdomains();
    fetchTickets();
    fetchWaitlistEmails();
    
    const metricsInterval = setInterval(() => {
      if (canViewVPS) fetchSystemMetrics();
      fetchUnreadTicketsCount();
    }, 30000);
    return () => clearInterval(metricsInterval);
  }, [canViewVPS]);

  const fetchData = async () => {
    try {
      const [usersRes, pagesRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/pages")
      ]);
      setUsers(usersRes.data);
      setPages(pagesRes.data);
    } catch (error) {
      toast.error(t('errors', 'loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      const params = ticketStatusFilter ? `?status=${ticketStatusFilter}` : '';
      const response = await api.get(`/admin/tickets${params}`);
      setTickets(response.data.tickets || []);
      setTicketsTotal(response.data.total || 0);
      fetchUnreadTicketsCount();
    } catch (error) {
      console.error("Failed to fetch tickets");
    }
  };

  const fetchUnreadTicketsCount = async () => {
    try {
      const response = await api.get("/admin/tickets/unread-count");
      setUnreadTickets(response.data.unread_count);
    } catch (error) {
      console.error("Failed to fetch unread count");
    }
  };

  const openTicket = async (ticketId) => {
    try {
      const response = await api.get(`/admin/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      // Update local list
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { ...t, is_read_by_staff: true } : t
      ));
      fetchUnreadTicketsCount();
    } catch (error) {
      toast.error(t('admin', 'ticketLoadError'));
    }
  };

  const sendTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      const response = await api.post(`/tickets/${selectedTicket.id}/reply`, {
        message: ticketReply
      });
      setSelectedTicket(response.data);
      setTicketReply("");
      setTickets(prev => prev.map(t => 
        t.id === selectedTicket.id ? response.data : t
      ));
      toast.success(t('admin', 'replySent'));
    } catch (error) {
      toast.error(t('admin', 'replyError'));
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      await api.put(`/admin/tickets/${ticketId}/status`, { status });
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { ...t, status } : t
      ));
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status }));
      }
      toast.success(t('admin', 'statusUpdated'));
    } catch (error) {
      toast.error(t('admin', 'statusUpdateError'));
    }
  };

  const fetchGlobalAnalytics = async () => {
    try {
      const response = await api.get("/admin/analytics/global");
      setGlobalAnalytics(response.data);
    } catch (error) {
      console.error("Failed to fetch global analytics");
    }
  };

  const fetchSystemMetrics = async () => {
    try {
      const response = await api.get("/admin/system/metrics");
      setSystemMetrics(response.data);
    } catch (error) {
      console.error("Failed to fetch system metrics");
    }
  };

  const fetchVerificationRequests = async () => {
    try {
      const response = await api.get("/admin/verification/requests");
      setVerificationRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch verification requests");
    }
  };

  const fetchPlanConfigs = async () => {
    try {
      const response = await api.get("/admin/plan-configs");
      setPlanConfigs(response.data);
    } catch (error) {
      console.error("Failed to fetch plan configs");
    }
  };

  const fetchSubdomains = async (search = "") => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await api.get(`/admin/subdomains${params}`);
      setSubdomains(response.data.subdomains || []);
      setSubdomainsTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch subdomains");
    }
  };

  const fetchWaitlistEmails = async () => {
    try {
      const response = await api.get("/admin/waitlist");
      setWaitlistEmails(response.data.emails || []);
      setWaitlistTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch waitlist emails");
    }
  };

  // Fetch user profile and their pages
  const openUserProfile = async (userId) => {
    setLoadingUserProfile(true);
    setUserProfileModalOpen(true);
    try {
      const [profileRes, pagesRes] = await Promise.all([
        api.get(`/admin/users/${userId}`),
        api.get(`/admin/users/${userId}/pages`)
      ]);
      setSelectedUserProfile(profileRes.data);
      setSelectedUserPages(pagesRes.data.pages || []);
    } catch (error) {
      toast.error(t('admin', 'profileLoadError') || 'Ошибка загрузки профиля');
      setUserProfileModalOpen(false);
    } finally {
      setLoadingUserProfile(false);
    }
  };

  const closeUserProfile = () => {
    setUserProfileModalOpen(false);
    setSelectedUserProfile(null);
    setSelectedUserPages([]);
  };

  const deleteWaitlistEmail = (emailId) => {
    askConfirm({
      title: t('admin', 'deleteEmailConfirm') || 'Удалить email?',
      destructive: true,
      confirmText: t('common', 'delete') || 'Удалить',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/waitlist/${emailId}`);
          toast.success(t('admin', 'emailDeleted'));
          fetchWaitlistEmails();
        } catch (error) {
          toast.error(t('errors', 'generic'));
        }
      },
    });
  };

  const toggleSubdomainAdmin = async (subdomainId, currentDisabled) => {
    try {
      // Backend expects is_active: if currently disabled, we want to enable (is_active: true)
      await api.put(`/admin/subdomains/${subdomainId}/toggle`, { is_active: currentDisabled });
      toast.success(currentDisabled ? t('admin', 'subdomainUnblocked') : t('admin', 'subdomainBlocked'));
      fetchSubdomains(subdomainSearch);
    } catch (error) {
      toast.error(t('errors', 'generic'));
    }
  };

  const deleteSubdomainAdmin = (subdomainId) => {
    askConfirm({
      title: t('admin', 'subdomainDeleteConfirm') || 'Удалить поддомен?',
      destructive: true,
      confirmText: t('common', 'delete') || 'Удалить',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/subdomains/${subdomainId}`);
          toast.success(t('admin', 'subdomainDeleted'));
          fetchSubdomains(subdomainSearch);
        } catch (error) {
          toast.error(t('errors', 'generic'));
        }
      },
    });
  };

  // User Management
  const toggleUserBan = async (userId, currentBanned) => {
    try {
      await api.put(`/admin/users/${userId}/ban`, { is_banned: !currentBanned });
      toast.success(currentBanned ? t('admin', 'userUnbanned') : t('admin', 'userBanned'));
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const deleteUser = (u) => {
    setUserActions(null);
    askConfirm({
      title: t('admin', 'deleteUserConfirm') || 'Удалить пользователя?',
      description: `${u.username || u.email} — ${t('admin', 'deleteUserWarning') || 'это удалит аккаунт и все его страницы безвозвратно.'}`,
      destructive: true,
      confirmText: t('common', 'delete') || 'Удалить',
      onConfirm: async () => {
        try {
          await api.delete(`/admin/users/${u.id}`);
          toast.success(t('admin', 'userDeleted') || 'Пользователь удалён');
          fetchData();
        } catch (error) {
          toast.error(getErrorMessage(error));
        }
      },
    });
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      toast.success(t('admin', 'roleChanged'));
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      await api.put(`/admin/users/${userId}/plan`, { plan: newPlan });
      toast.success(t('admin', 'planChanged'));
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const updateOwnerPlan = async (newPlan) => {
    try {
      await api.put(`/owner/my-plan`, { plan: newPlan });
      toast.success(t('admin', 'planChangedForTesting'));
      window.location.reload(); // Reload to update user context
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const toggleUserVerify = async (userId, currentVerified) => {
    try {
      await api.put(`/admin/users/${userId}/verify`, { is_verified: !currentVerified });
      toast.success(currentVerified ? t('admin', 'verificationRevoked') : t('admin', 'verified'));
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const updatePlanConfig = async (planName, updates) => {
    try {
      await api.put(`/admin/plan-configs/${planName}`, updates);
      toast.success(t('common', 'success'));
      fetchPlanConfigs();
      setEditingPlan(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const approveVerification = async (userId) => {
    try {
      await api.put(`/admin/verification/${userId}/approve`);
      toast.success(t('admin', 'approved'));
      fetchVerificationRequests();
      fetchData();
    } catch (error) {
      toast.error(t('errors', 'generic'));
    }
  };

  const rejectVerification = async (userId) => {
    try {
      await api.put(`/admin/verification/${userId}/reject`);
      toast.success(t('admin', 'rejected'));
      fetchVerificationRequests();
      fetchData();
    } catch (error) {
      toast.error(t('errors', 'generic'));
    }
  };

  const togglePageStatus = async (pageId) => {
    try {
      await api.put(`/admin/pages/${pageId}/disable`);
      toast.success(t('common', 'success'));
      fetchData();
    } catch (error) {
      toast.error(t('errors', 'generic'));
    }
  };

  // Country flags - map country names to emoji flags
  const COUNTRY_FLAGS = {
    "Россия": "🇷🇺", "Russia": "🇷🇺", "Rusia": "🇷🇺",
    "США": "🇺🇸", "USA": "🇺🇸", "EE.UU.": "🇺🇸",
    "Украина": "🇺🇦", "Ukraine": "🇺🇦", "Ucrania": "🇺🇦",
    "Беларусь": "🇧🇾", "Belarus": "🇧🇾", "Bielorrusia": "🇧🇾",
    "Казахстан": "🇰🇿", "Kazakhstan": "🇰🇿", "Kazajistán": "🇰🇿",
    "Германия": "🇩🇪", "Germany": "🇩🇪", "Alemania": "🇩🇪", "ФРГ": "🇩🇪",
    "Великобритания": "🇬🇧", "United Kingdom": "🇬🇧", "Reino Unido": "🇬🇧", "UK": "🇬🇧",
    "Франция": "🇫🇷", "France": "🇫🇷", "Francia": "🇫🇷",
    "Гонконг": "🇭🇰", "Hong Kong": "🇭🇰",
    "Сингапур": "🇸🇬", "Singapore": "🇸🇬", "Singapur": "🇸🇬",
    "Неизвестно": "🌍", "Unknown": "🌍", "Desconocido": "🌍",
  };
  const getCountryFlag = (country) => COUNTRY_FLAGS[country] || "🌍";
  
  // Translate country name
  const translateCountry = (country) => {
    const countryMap = {
      "Россия": "countryRussia", "Russia": "countryRussia", "Rusia": "countryRussia",
      "США": "countryUSA", "USA": "countryUSA", "EE.UU.": "countryUSA",
      "Украина": "countryUkraine", "Ukraine": "countryUkraine", "Ucrania": "countryUkraine",
      "Беларусь": "countryBelarus", "Belarus": "countryBelarus", "Bielorrusia": "countryBelarus",
      "Казахстан": "countryKazakhstan", "Kazakhstan": "countryKazakhstan", "Kazajistán": "countryKazakhstan",
      "Германия": "countryGermany", "Germany": "countryGermany", "Alemania": "countryGermany", "ФРГ": "countryGermany",
      "Великобритания": "countryUK", "United Kingdom": "countryUK", "Reino Unido": "countryUK", "UK": "countryUK",
      "Франция": "countryFrance", "France": "countryFrance", "Francia": "countryFrance",
      "Гонконг": "countryHongKong", "Hong Kong": "countryHongKong",
      "Сингапур": "countrySingapore", "Singapore": "countrySingapore", "Singapur": "countrySingapore",
      "Неизвестно": "countryUnknown", "Unknown": "countryUnknown", "Desconocido": "countryUnknown",
    };
    const key = countryMap[country];
    return key ? t('admin', key) : country || t('admin', 'countryUnknown');
  };

  const getProgressColor = (percent) => {
    if (percent >= 90) return "bg-red-500";
    if (percent >= 70) return "bg-yellow-500";
    return "bg-emerald-500";
  };

  // Prepare chart data
  const planDistribution = [
    { name: 'Free', value: users.filter(u => u.plan === 'free').length, color: '#71717a' },
    { name: 'Pro', value: users.filter(u => u.plan === 'pro').length, color: '#a855f7' }
  ].filter(p => p.value > 0);

  const roleDistribution = [
    { name: t('admin', 'users'), value: users.filter(u => u.role === 'user').length },
    { name: t('admin', 'moderators'), value: users.filter(u => u.role === 'moderator').length },
    { name: t('admin', 'admins'), value: users.filter(u => u.role === 'admin').length },
    { name: t('admin', 'owners'), value: users.filter(u => u.role === 'owner').length }
  ].filter(r => r.value > 0);

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary animate-spin"></div>
              <Zap className="w-6 h-6 text-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-muted-foreground">{t('common', 'loading')}</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen bg-background">
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">
                      {t('admin', 'title')}
                    </h1>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {currentUser?.email}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-full text-sm border ${ROLE_CONFIG[currentUser?.role]?.color}`}>
                {t('admin', ROLE_CONFIG[currentUser?.role]?.labelKey)}
              </div>
            </div>
            
            {/* Owner Test Plan Switcher */}
            {isOwner && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 rounded-xl bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-500/20"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Crown className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-300">{t('admin', 'testSubscriptions')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{t('admin', 'yourPlan')}:</span>
                    <select
                      value={currentUser?.plan || 'free'}
                      onChange={(e) => updateOwnerPlan(e.target.value)}
                      className="h-8 px-3 rounded-lg bg-muted border border-yellow-500/30 text-sm cursor-pointer hover:border-yellow-500/50 transition-colors text-white"
                    >
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                    </select>
                    <span className="text-xs text-muted-foreground">({t('admin', 'forTesting')})</span>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
              <TabsList className="bg-card/80 backdrop-blur-sm border border-border p-1 rounded-xl inline-flex min-w-max">
                <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">{t('admin', 'globalAnalytics')}</span>
                  <span className="sm:hidden">{t('nav', 'analytics')}</span>
                </TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                  <Users className="w-4 h-4 mr-1.5" />
                  {t('admin', 'users')}
                </TabsTrigger>
                <TabsTrigger value="plans" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                  <Sliders className="w-4 h-4 mr-1.5" />
                  {t('admin', 'plans')}
                </TabsTrigger>
                <TabsTrigger value="pages" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                  <FileText className="w-4 h-4 mr-1.5" />
                  {t('admin', 'pages')}
                </TabsTrigger>
                <TabsTrigger value="domains" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                  <Link2 className="w-4 h-4 mr-1.5" />
                  {t('nav', 'domains')}
                  {subdomainsTotal > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-white/10 text-[10px]">{subdomainsTotal}</span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="tickets" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4 relative">
                  <MessageCircle className="w-4 h-4 mr-1.5" />
                  {t('admin', 'tickets')}
                  {unreadTickets > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center animate-pulse">
                      {unreadTickets}
                    </span>
                  )}
                </TabsTrigger>
                {verificationRequests.filter(r => r.status === 'pending').length > 0 && (
                  <TabsTrigger value="verification" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4 relative">
                    <BadgeCheck className="w-4 h-4 mr-1.5" />
                    {t('admin', 'requests')}
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                      {verificationRequests.filter(r => r.status === 'pending').length}
                    </span>
                  </TabsTrigger>
                )}
                {canViewVPS && (
                  <TabsTrigger value="system" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                    <Server className="w-4 h-4 mr-1.5" />
                    VPS
                  </TabsTrigger>
                )}
                <TabsTrigger value="emails" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white text-xs sm:text-sm px-3 sm:px-4">
                  <Mail className="w-4 h-4 mr-1.5" />
                  E-Mails
                  {waitlistTotal > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-primary/20 rounded-full">
                      {waitlistTotal}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Global Analytics Tab */}
            <TabsContent value="analytics" className="mt-0">
              <div className="space-y-6">
                {/* Main Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                  {[
                    { icon: Users, label: t('admin', 'totalUsers'), value: globalAnalytics?.total_users || users.length, color: "from-blue-500 to-cyan-500", bg: "bg-blue-500/10" },
                    { icon: FileText, label: t('admin', 'totalPages'), value: globalAnalytics?.total_pages || pages.length, color: "from-purple-500 to-pink-500", bg: "bg-purple-500/10" },
                    { icon: Eye, label: t('admin', 'totalViews'), value: globalAnalytics?.total_views || 0, color: "from-emerald-500 to-teal-500", bg: "bg-emerald-500/10" },
                    { icon: MousePointer, label: t('admin', 'totalClicks'), value: globalAnalytics?.total_clicks || 0, color: "from-orange-500 to-amber-500", bg: "bg-orange-500/10" },
                    { icon: Share2, label: t('admin', 'totalShares'), value: globalAnalytics?.total_shares || 0, color: "from-pink-500 to-rose-500", bg: "bg-pink-500/10" },
                    { icon: QrCode, label: t('admin', 'totalQrScans'), value: globalAnalytics?.total_qr_scans || 0, color: "from-violet-500 to-purple-500", bg: "bg-violet-500/10" }
                  ].map((stat, idx) => (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`relative overflow-hidden p-4 sm:p-5 rounded-2xl ${stat.bg} border border-border group hover:border-border transition-all`}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
                      <stat.icon className={`w-5 h-5 mb-3 bg-gradient-to-r ${stat.color} bg-clip-text`} style={{ color: 'transparent', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }} />
                      <p className="text-2xl sm:text-3xl font-bold mb-1">{(stat.value || 0).toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Timeline Chart */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="p-5 sm:p-6 rounded-2xl panel-card backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-primary" />
                        {t('admin', 'activity30Days')}
                      </h3>
                      <span className="text-xs text-muted-foreground px-2 py-1 rounded-full bg-muted">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {t('admin', 'days30')}
                      </span>
                    </div>
                    {globalAnalytics?.timeline?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={globalAnalytics.timeline}>
                          <defs>
                            <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#d946ef" stopOpacity={0.4}/>
                              <stop offset="95%" stopColor="#d946ef" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis dataKey="date" stroke="#52525b" fontSize={10} tickFormatter={(v) => v.slice(5)} />
                          <YAxis stroke="#52525b" fontSize={10} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '12px' }}
                            labelStyle={{ color: '#a1a1aa' }}
                          />
                          <Area type="monotone" dataKey="clicks" stroke="#d946ef" strokeWidth={2} fillOpacity={1} fill="url(#colorClicks)" name={t('admin', 'clicks')} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[220px] flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>{t('admin', 'noData')}</p>
                        </div>
                      </div>
                    )}
                  </motion.div>

                  {/* Geography */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="p-5 sm:p-6 rounded-2xl panel-card backdrop-blur-sm"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-400" />
                        {t('admin', 'geographyClicks')}
                      </h3>
                    </div>
                    {globalAnalytics?.by_country?.length > 0 ? (
                      <div className="space-y-3">
                        {globalAnalytics.by_country.slice(0, 6).map((item, idx) => {
                          const maxClicks = globalAnalytics.by_country[0]?.clicks || 1;
                          const percentage = ((item.clicks / maxClicks) * 100);
                          return (
                            <div key={idx} className="group">
                              <div className="flex items-center gap-3">
                                <span className="text-lg w-7">{countryFlag(item.country_code)}</span>
                                <div className="flex-1">
                                  <div className="flex justify-between text-sm mb-1.5">
                                    <span className="font-medium">{localizeCountry(item.country_code, language, item.country)}</span>
                                    <span className="text-muted-foreground">{item.clicks}</span>
                                  </div>
                                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{ width: `${percentage}%` }}
                                      transition={{ duration: 0.6, delay: 0.4 + idx * 0.05 }}
                                      className="h-full rounded-full bg-gradient-to-r from-primary to-purple-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                        <div className="text-center">
                          <Globe className="w-10 h-10 mx-auto mb-2 opacity-30" />
                          <p>{t('admin', 'noData')}</p>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>

                {/* Distribution Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  {/* Plans Distribution */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="p-5 sm:p-6 rounded-2xl panel-card"
                  >
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <PieChart className="w-4 h-4 text-purple-400" />
                      {t('admin', 'planDistribution')}
                    </h3>
                    <div className="flex items-center justify-center">
                      <ResponsiveContainer width="100%" height={180}>
                        <RechartsPie>
                          <Pie
                            data={planDistribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={70}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {planDistribution.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a', borderRadius: '8px' }} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex justify-center gap-4 mt-2">
                      {planDistribution.map((plan) => (
                        <div key={plan.name} className="flex items-center gap-2 text-xs">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plan.color }} />
                          <span>{plan.name}: {plan.value}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* User Status */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45 }}
                    className="p-5 sm:p-6 rounded-2xl panel-card"
                  >
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-emerald-400" />
                      {t('admin', 'userStatus')}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                        <p className="text-3xl font-bold text-emerald-400">{users.filter(u => !u.is_banned && u.status === 'active').length}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t('admin', 'active')}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                        <p className="text-3xl font-bold text-red-400">{users.filter(u => u.is_banned).length}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t('admin', 'bannedUsers')}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20">
                        <p className="text-3xl font-bold text-primary">{users.filter(u => u.is_verified || u.verified).length}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t('admin', 'verifiedUsers')}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/50 border border-border">
                        <p className="text-3xl font-bold">{pages.filter(p => p.status === 'active').length}</p>
                        <p className="text-xs text-muted-foreground mt-1">{t('admin', 'activePages')}</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="mt-0">
              <div className="space-y-4">
                {users.map((user, i) => {
                  const RoleIcon = ROLE_CONFIG[user.role]?.icon || Users;
                  const isBanned = user.is_banned || user.status === 'blocked';
                  const isVerified = user.is_verified || user.verified;
                  
                  return (
                    <motion.div
                      key={user.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className={`p-4 rounded-2xl border transition-all ${
                        isBanned ? 'bg-red-950/20 border-red-500/20' : 'bg-card/50 border-border hover:border-border'
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                        {/* User Info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            user.role === 'owner' ? 'bg-gradient-to-br from-yellow-500/20 to-amber-500/20' :
                            user.role === 'admin' ? 'bg-purple-500/20' : 'bg-muted'
                          }`}>
                            <RoleIcon className={`w-5 h-5 ${
                              user.role === 'owner' ? 'text-yellow-400' :
                              user.role === 'admin' ? 'text-purple-400' : 'text-zinc-400'
                            }`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold truncate">{user.username}</p>
                              {isVerified && <BadgeCheck className="w-4 h-4 text-primary flex-shrink-0" />}
                              {isBanned && <span className="px-2 py-0.5 rounded-full text-[10px] bg-red-500/20 text-red-400">{t('admin', 'ban')}</span>}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          </div>
                        </div>

                        {/* Badges */}
                        <div className="flex items-center gap-2 flex-wrap pl-14 lg:pl-0">
                          <span className={`px-2 py-1 rounded-lg text-[11px] border ${ROLE_CONFIG[user.role]?.color}`}>
                            {t('admin', ROLE_CONFIG[user.role]?.labelKey)}
                          </span>
                          <span className={`px-2 py-1 rounded-lg text-[11px] ${PLAN_CONFIG[user.plan]?.color}`}>
                            {PLAN_CONFIG[user.plan]?.label}
                          </span>
                          <span className="px-2 py-1 rounded-lg text-[11px] bg-muted text-zinc-400">
                            {user.page_count || 0} {t('admin', 'pagesCount')}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 pl-14 lg:pl-0 flex-wrap">
                          {/* View Profile Button - Available for all users */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openUserProfile(user.id)}
                            className="h-8 text-xs"
                            title={t('admin', 'viewProfile') || 'Просмотр профиля'}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            {t('admin', 'profile') || 'Профиль'}
                          </Button>
                          
                          {user.role !== 'owner' && (
                            <>
                              <Button
                                size="sm"
                                variant={isVerified ? "outline" : "default"}
                                onClick={() => toggleUserVerify(user.id, isVerified)}
                                className="h-8 text-xs"
                              >
                                <BadgeCheck className="w-3.5 h-3.5 mr-1" />
                                {isVerified ? t('admin', 'revoke') : t('admin', 'grant')}
                              </Button>

                              <select
                                value={user.plan}
                                onChange={(e) => updateUserPlan(user.id, e.target.value)}
                                className="h-8 px-2 rounded-lg bg-muted border border-border text-xs cursor-pointer hover:border-primary/50 transition-colors"
                              >
                                <option value="free">Free</option>
                                <option value="pro">Pro</option>
                              </select>

                              {isOwner && (
                                <select
                                  value={user.role}
                                  onChange={(e) => updateUserRole(user.id, e.target.value)}
                                  className="h-8 px-2 rounded-lg bg-muted border border-border text-xs cursor-pointer hover:border-primary/50 transition-colors"
                                >
                                  <option value="user">User</option>
                                  <option value="moderator">Mod</option>
                                  <option value="admin">Admin</option>
                                </select>
                              )}

                              {user.role !== 'admin' && (
                                <Button
                                  size="sm"
                                  variant={isBanned ? "default" : "destructive"}
                                  onClick={() => setUserActions(user)}
                                  className="h-8 text-xs"
                                  title={t('admin', 'userActions') || 'Действия'}
                                >
                                  {isBanned ? <Check className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </TabsContent>

            {/* Plans Tab */}
            <TabsContent value="plans" className="mt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                {planConfigs.map((config, idx) => (
                  <motion.div
                    key={config.plan_name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`p-6 rounded-2xl border relative overflow-hidden ${
                      config.plan_name === 'pro' ? 'bg-gradient-to-br from-blue-950/30 to-cyan-950/30 border-blue-500/20' :
                      'bg-card/50 border-border'
                    }`}
                  >
                    {config.plan_name === 'pro' && (
                      <div className="absolute top-4 right-4">
                        <Crown className="w-6 h-6 text-blue-400" />
                      </div>
                    )}

                    <h3 className="text-xl font-bold capitalize mb-1">{config.plan_name}</h3>
                    <p className="text-xs text-muted-foreground mb-4">
                      {users.filter(u => u.plan === config.plan_name).length} {t('admin', 'usersCount')}
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">{t('admin', 'pagesLimit')}</label>
                        <input
                          type="number"
                          value={config.max_pages_limit}
                          onChange={(e) => {
                            setPlanConfigs(prev => prev.map(p => 
                              p.plan_name === config.plan_name ? {...p, max_pages_limit: parseInt(e.target.value)} : p
                            ));
                            setEditingPlan(config.plan_name);
                          }}
                          className="w-full h-10 px-3 rounded-xl bg-muted/50 border border-border text-sm focus:border-primary focus:outline-none transition-colors"
                          min="-1"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">{t('admin', 'unlimited')}</p>
                      </div>

                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">{t('admin', 'subdomainsLimit')}</label>
                        <input
                          type="number"
                          value={config.max_subdomains_limit ?? 0}
                          onChange={(e) => {
                            setPlanConfigs(prev => prev.map(p => 
                              p.plan_name === config.plan_name ? {...p, max_subdomains_limit: parseInt(e.target.value)} : p
                            ));
                            setEditingPlan(config.plan_name);
                          }}
                          className="w-full h-10 px-3 rounded-xl bg-muted/50 border border-border text-sm focus:border-primary focus:outline-none transition-colors"
                          min="-1"
                        />
                        <p className="text-[10px] text-muted-foreground mt-1">{t('admin', 'unlimited')}, {t('admin', 'disabled')}</p>
                      </div>

                      <div className="space-y-2.5">
                        {[
                          { key: 'has_analytics', label: t('admin', 'basicAnalytics') },
                          { key: 'has_advanced_analytics', label: t('admin', 'advancedAnalytics') },
                          { key: 'can_use_ai_generation', label: t('admin', 'aiGeneration') },
                          { key: 'can_verify_profile', label: t('admin', 'profileVerification') },
                          { key: 'can_remove_branding', label: t('admin', 'removeBranding') },
                          { key: 'priority_support', label: t('admin', 'prioritySupport') }
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center justify-between cursor-pointer group">
                            <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">{label}</span>
                            <div className="relative">
                              <input
                                type="checkbox"
                                checked={config[key] || false}
                                onChange={(e) => {
                                  setPlanConfigs(prev => prev.map(p => 
                                    p.plan_name === config.plan_name ? {...p, [key]: e.target.checked} : p
                                  ));
                                  setEditingPlan(config.plan_name);
                                }}
                                className="sr-only peer"
                              />
                              <div className="w-9 h-5 bg-muted rounded-full peer peer-checked:bg-primary transition-colors"></div>
                              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-4"></div>
                            </div>
                          </label>
                        ))}
                      </div>

                      <AnimatePresence>
                        {editingPlan === config.plan_name && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Button
                              onClick={() => {
                                const currentConfig = planConfigs.find(p => p.plan_name === config.plan_name);
                                updatePlanConfig(config.plan_name, {
                                  max_pages_limit: currentConfig.max_pages_limit,
                                  max_subdomains_limit: currentConfig.max_subdomains_limit,
                                  has_analytics: currentConfig.has_analytics,
                                  has_advanced_analytics: currentConfig.has_advanced_analytics,
                                  can_use_ai_generation: currentConfig.can_use_ai_generation,
                                  can_verify_profile: currentConfig.can_verify_profile,
                                  can_remove_branding: currentConfig.can_remove_branding,
                                  priority_support: currentConfig.priority_support
                                });
                              }}
                              className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              {t('admin', 'saveChanges')}
                            </Button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* Pages Tab */}
            <TabsContent value="pages" className="mt-0">
              <div className="space-y-3">
                {pages.map((page, i) => (
                  <motion.div
                    key={page.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className="p-4 rounded-2xl panel-card hover:border-border transition-all"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden flex-shrink-0">
                          {page.cover_image ? (
                            <img src={page.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${page.cover_image}` : page.cover_image}
                              alt={page.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Music className="w-6 h-6 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold truncate">{page.title}</p>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                              page.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                            }`}>{page.status}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {page.user?.username || "—"} • /{page.slug}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 pl-[72px] sm:pl-0">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Eye className="w-4 h-4" /> {page.views || 0}
                          </span>
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <MousePointer className="w-4 h-4" /> {page.total_clicks || 0}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          <a href={getPublicPageUrl(page.slug)} target="_blank" rel="noopener noreferrer">
                            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            variant={page.status === "active" ? "destructive" : "default"}
                            onClick={() => togglePageStatus(page.id)}
                            className="h-9 rounded-xl"
                          >
                            {page.status === "active" ? t('admin', 'disablePage') : t('admin', 'enablePage')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {pages.length === 0 && (
                  <div className="text-center py-20 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t('admin', 'noData')}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Domains Tab */}
            <TabsContent value="domains" className="mt-0">
              <div className="space-y-4">
                {/* Search */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder={t('admin', 'searchSubdomains')}
                      value={subdomainSearch}
                      onChange={(e) => {
                        setSubdomainSearch(e.target.value);
                        fetchSubdomains(e.target.value);
                      }}
                      className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card/50 border border-border focus:border-primary focus:outline-none text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl panel-card">
                    <Globe className="w-4 h-4 text-blue-400" />
                    <span className="text-sm">{t('common', 'total')}: <span className="font-semibold">{subdomainsTotal}</span></span>
                  </div>
                </div>

                {/* Subdomains List */}
                {subdomains.map((sub, i) => (
                  <motion.div
                    key={sub.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.02 }}
                    className={`p-4 rounded-2xl border transition-all ${
                      sub.disabled_by_admin 
                        ? 'bg-red-950/20 border-red-500/20' 
                        : sub.is_active 
                          ? 'bg-card/50 border-border hover:border-border' 
                          : 'bg-card/30 border-border'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          sub.disabled_by_admin 
                            ? 'bg-red-500/20' 
                            : sub.is_active 
                              ? 'bg-gradient-to-br from-emerald-500/20 to-teal-500/20' 
                              : 'bg-muted'
                        }`}>
                          <Globe className={`w-5 h-5 ${
                            sub.disabled_by_admin ? 'text-red-400' : sub.is_active ? 'text-emerald-400' : 'text-zinc-500'
                          }`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-mono font-semibold">{sub.subdomain}.mus.link</p>
                            {sub.disabled_by_admin && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-red-500/20 text-red-400">
                                {t('admin', 'banned')}
                              </span>
                            )}
                            {!sub.is_active && !sub.disabled_by_admin && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] bg-muted text-muted-foreground">
                                {t('common', 'disabled')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {t('admin', 'user')}: <span className="text-foreground">{sub.owner?.username || sub.owner?.email || sub.user_id}</span>
                            {sub.owner?.email && <span className="text-zinc-500"> ({sub.owner.email})</span>}
                            {" • "}
                            {new Date(sub.created_at).toLocaleDateString('ru-RU')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pl-14 sm:pl-0">
                        <a 
                          href={`https://${sub.subdomain}.mus.link`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button
                          size="sm"
                          variant={sub.disabled_by_admin ? "default" : "destructive"}
                          onClick={() => toggleSubdomainAdmin(sub.id, sub.disabled_by_admin)}
                          className="h-9 rounded-xl"
                        >
                          {sub.disabled_by_admin ? (
                            <>
                              <Check className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">{t('admin', 'enableSubdomain')}</span>
                            </>
                          ) : (
                            <>
                              <Ban className="w-4 h-4 sm:mr-1" />
                              <span className="hidden sm:inline">{t('admin', 'disableSubdomain')}</span>
                            </>
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteSubdomainAdmin(sub.id)}
                          className="h-9 w-9 rounded-xl text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {subdomains.length === 0 && (
                  <div className="text-center py-20 text-muted-foreground">
                    <Link2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t('admin', 'noData')}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tickets Tab */}
            <TabsContent value="tickets" className="mt-0">
              <div className="space-y-4">
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <select
                    value={ticketStatusFilter}
                    onChange={(e) => {
                      setTicketStatusFilter(e.target.value);
                      fetchTickets();
                    }}
                    className="px-4 py-2.5 rounded-xl bg-card/50 border border-border focus:border-primary focus:outline-none text-sm"
                  >
                    <option value="">{t('admin', 'allStatuses')}</option>
                    <option value="open">{t('admin', 'openTickets')}</option>
                    <option value="in_progress">{t('admin', 'inProgressTickets')}</option>
                    <option value="resolved">{t('admin', 'resolvedTickets')}</option>
                    <option value="closed">{t('admin', 'closedTickets')}</option>
                  </select>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-xl panel-card">
                    <MessageCircle className="w-4 h-4 text-blue-400" />
                    <span className="text-sm">{t('common', 'total')}: <span className="font-semibold">{ticketsTotal}</span></span>
                    {unreadTickets > 0 && (
                      <span className="ml-2 px-2 py-1 rounded-full bg-red-500 text-white text-xs">
                        {unreadTickets} {t('admin', 'unreadCount')}
                      </span>
                    )}
                  </div>
                </div>

                {/* Tickets List */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Tickets List */}
                  <div className="space-y-3">
                    {tickets.map((ticket, i) => {
                      const statusConfig = TICKET_STATUS_CONFIG[ticket.status] || TICKET_STATUS_CONFIG.open;
                      const StatusIcon = statusConfig.icon;
                      
                      return (
                        <motion.div
                          key={ticket.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className={`p-4 rounded-2xl border transition-all cursor-pointer ${
                            selectedTicket?.id === ticket.id
                              ? 'bg-primary/10 border-primary/30'
                              : !ticket.is_read_by_staff
                                ? 'bg-blue-950/20 border-blue-500/20 hover:border-blue-500/40'
                                : 'bg-card/50 border-border hover:border-border'
                          }`}
                          onClick={() => openTicket(ticket.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0`}
                                 style={{ backgroundColor: statusConfig.color + '20' }}>
                              <StatusIcon className="w-5 h-5" style={{ color: statusConfig.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold truncate">{ticket.subject}</h3>
                                {!ticket.is_read_by_staff && (
                                  <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mb-2">
                                {t('admin', 'ticketFrom')}: {ticket.user_email || ticket.user?.email || ticket.user?.username || "—"}
                              </p>
                              <div className="flex items-center justify-between">
                                <span className="px-2 py-1 rounded-lg text-xs border"
                                      style={{ 
                                        backgroundColor: statusConfig.color + '20',
                                        borderColor: statusConfig.color + '30',
                                        color: statusConfig.color
                                      }}>
                                  {t('admin', statusConfig.labelKey)}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(ticket.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                    
                    {tickets.length === 0 && (
                      <div className="text-center py-20 text-muted-foreground">
                        <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{t('admin', 'noTicketsFound')}</p>
                      </div>
                    )}
                  </div>

                  {/* Ticket Details */}
                  <div className="lg:sticky lg:top-4">
                    {selectedTicket ? (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-6 rounded-2xl panel-card"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h2 className="text-xl font-semibold mb-2">{selectedTicket.subject}</h2>
                            <p className="text-sm text-muted-foreground">
                              {t('admin', 'ticketFrom')}: {selectedTicket.user_email || selectedTicket.user?.email || selectedTicket.user?.username || "—"}
                            </p>
                          </div>
                          <select
                            value={selectedTicket.status}
                            onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                            className="px-3 py-1.5 rounded-lg bg-muted border border-border text-sm"
                          >
                            <option value="open">{t('admin', 'ticketOpen')}</option>
                            <option value="in_progress">{t('admin', 'ticketInProgress')}</option>
                            <option value="resolved">{t('admin', 'ticketResolved')}</option>
                            <option value="closed">{t('admin', 'ticketClosed')}</option>
                          </select>
                        </div>

                        {/* Messages */}
                        <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                          {selectedTicket.messages?.map((message, idx) => (
                            <div key={idx} className={`p-4 rounded-xl ${
                              (message.sender_role === 'staff' || message.is_staff_reply)
                                ? 'bg-primary/10 border border-primary/20 ml-4' 
                                : 'bg-muted/50 mr-4'
                            }`}>
                              <div className="flex items-center gap-2 mb-2">
                                <User className="w-4 h-4" />
                                <span className="text-sm font-medium">
                                  {(message.sender_role === 'staff' || message.is_staff_reply)
                                    ? t('admin', 'supportAgent')
                                    : (message.sender_email || selectedTicket.user_email || selectedTicket.user?.email || t('admin', 'roleUser'))}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(message.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                            </div>
                          ))}
                        </div>

                        {/* Reply Form */}
                        <div className="space-y-3">
                          <Textarea
                            placeholder={t('admin', 'yourReply')}
                            value={ticketReply}
                            onChange={(e) => setTicketReply(e.target.value)}
                            className="min-h-[100px] bg-muted/50 border-border focus:border-primary"
                          />
                          <Button 
                            onClick={sendTicketReply}
                            disabled={!ticketReply.trim()}
                            className="w-full bg-gradient-to-r from-primary to-purple-600"
                          >
                            <Send className="w-4 h-4 mr-2" />
                            {t('admin', 'sendReplyBtn')}
                          </Button>
                        </div>
                      </motion.div>
                    ) : (
                      <div className="p-6 rounded-2xl panel-card text-center text-muted-foreground">
                        <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>{t('admin', 'selectTicket')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Verification Tab */}
            <TabsContent value="verification" className="mt-0">
              <div className="space-y-4">
                {verificationRequests.filter(r => r.status === 'pending').map((req, i) => (
                  <motion.div
                    key={req.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="p-5 rounded-2xl panel-card"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">{req.artist_name}</span>
                          <span className="text-sm text-muted-foreground">@{req.username}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{req.email}</p>
                        {req.social_links && (
                          <p className="text-sm"><span className="text-muted-foreground">{t('admin', 'socialLinks')}:</span> {req.social_links}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(req.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => approveVerification(req.user_id)} className="bg-emerald-600 hover:bg-emerald-700">
                          <Check className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">{t('admin', 'approveBtn')}</span>
                        </Button>
                        <Button variant="destructive" onClick={() => rejectVerification(req.user_id)}>
                          <X className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">{t('admin', 'rejectBtn')}</span>
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {verificationRequests.filter(r => r.status === 'pending').length === 0 && (
                  <div className="text-center py-20 text-muted-foreground">
                    <BadgeCheck className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t('admin', 'noPendingRequests')}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* System Tab - Only for Owner/Admin */}
            {canViewVPS && (
              <TabsContent value="system" className="mt-0">
                {systemMetrics ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {/* CPU */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-6 rounded-2xl bg-gradient-to-br from-blue-950/30 to-cyan-950/30 border border-blue-500/20"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Cpu className="w-5 h-5 text-blue-400" />
                          <h3 className="font-semibold">CPU</h3>
                        </div>
                        <span className="text-xs text-muted-foreground">{systemMetrics.cpu?.count} cores</span>
                      </div>
                      <p className="text-4xl font-bold mb-3">{systemMetrics.cpu?.percent?.toFixed(1)}%</p>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${systemMetrics.cpu?.percent}%` }}
                          transition={{ duration: 0.5 }}
                          className={`h-full ${getProgressColor(systemMetrics.cpu?.percent)} rounded-full`}
                        />
                      </div>
                    </motion.div>

                    {/* Memory */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="p-6 rounded-2xl bg-gradient-to-br from-emerald-950/30 to-teal-950/30 border border-emerald-500/20"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Activity className="w-5 h-5 text-emerald-400" />
                          <h3 className="font-semibold">{t('admin', 'memory')}</h3>
                        </div>
                      </div>
                      <p className="text-4xl font-bold mb-1">{systemMetrics.memory?.percent?.toFixed(1)}%</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        {systemMetrics.memory?.used_gb?.toFixed(1) || '0'} / {systemMetrics.memory?.total_gb?.toFixed(1) || '0'} GB
                      </p>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${systemMetrics.memory?.percent}%` }}
                          transition={{ duration: 0.5, delay: 0.1 }}
                          className={`h-full ${getProgressColor(systemMetrics.memory?.percent)} rounded-full`}
                        />
                      </div>
                    </motion.div>

                    {/* Disk */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="p-6 rounded-2xl bg-gradient-to-br from-purple-950/30 to-pink-950/30 border border-purple-500/20"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <HardDrive className="w-5 h-5 text-purple-400" />
                          <h3 className="font-semibold">{t('admin', 'disk')}</h3>
                        </div>
                      </div>
                      <p className="text-4xl font-bold mb-1">{systemMetrics.disk?.percent?.toFixed(1)}%</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        {systemMetrics.disk?.used_gb?.toFixed(1) || '0'} / {systemMetrics.disk?.total_gb?.toFixed(1) || '0'} GB
                      </p>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${systemMetrics.disk?.percent}%` }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                          className={`h-full ${getProgressColor(systemMetrics.disk?.percent)} rounded-full`}
                        />
                      </div>
                    </motion.div>

                    {/* Network */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="p-6 rounded-2xl panel-card md:col-span-2"
                    >
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Globe className="w-5 h-5 text-blue-400" />
                        {t('admin', 'network')}
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">↑ {t('admin', 'sent')}</p>
                          <p className="text-2xl font-bold">{systemMetrics.network?.sent_mb?.toFixed(1) || '0'} MB</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">↓ {t('admin', 'received')}</p>
                          <p className="text-2xl font-bold">{systemMetrics.network?.recv_mb?.toFixed(1) || '0'} MB</p>
                        </div>
                      </div>
                    </motion.div>

                    {/* Uptime */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="p-6 rounded-2xl panel-card"
                    >
                      <h3 className="font-semibold mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-emerald-400" />
                        {t('admin', 'uptime')}
                      </h3>
                      <p className="text-2xl font-bold text-emerald-400">{systemMetrics.uptime || '—'}</p>
                    </motion.div>
                  </div>
                ) : (
                  <div className="text-center py-20 text-muted-foreground">
                    <Server className="w-12 h-12 mx-auto mb-3 opacity-30 animate-pulse" />
                    <p>{t('admin', 'loadingMetrics')}</p>
                  </div>
                )}
              </TabsContent>
            )}

            {/* E-Mails Tab */}
            <TabsContent value="emails" className="mt-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 rounded-2xl panel-card"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{t('admin', 'waitlistEmails')}</h3>
                      <p className="text-sm text-muted-foreground">{t('admin', 'waitlistDesc')}</p>
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {t('admin', 'total')}: {waitlistTotal}
                  </span>
                </div>

                {waitlistEmails.length > 0 ? (
                  <div className="space-y-3">
                    {waitlistEmails.map((item) => (
                      <div 
                        key={item.id} 
                        className="flex items-center justify-between p-4 rounded-xl bg-card/30 border border-border hover:border-primary/30 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                            <Mail className="w-5 h-5 text-purple-400" />
                          </div>
                          <div>
                            <p className="font-medium">{item.email}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>{new Date(item.created_at).toLocaleDateString()}</span>
                              {item.features?.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span className="text-primary">{item.features.join(', ')}</span>
                                </>
                              )}
                              {item.language && (
                                <>
                                  <span>•</span>
                                  <span className="uppercase">{item.language}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteWaitlistEmail(item.id)}
                          className="text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>{t('admin', 'noWaitlistEmails')}</p>
                  </div>
                )}
              </motion.div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* User Profile Modal */}
      <AnimatePresence>
        {userProfileModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={closeUserProfile}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-card rounded-2xl border border-border shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {loadingUserProfile ? (
                <div className="p-12 flex flex-col items-center justify-center">
                  <div className="w-12 h-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin mb-4"></div>
                  <p className="text-muted-foreground">{t('common', 'loading')}</p>
                </div>
              ) : selectedUserProfile ? (
                <>
                  {/* Header */}
                  <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-6 border-b border-border bg-card/95 backdrop-blur-sm">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={closeUserProfile}
                        className="rounded-xl"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </Button>
                      <div>
                        <h2 className="text-lg font-bold flex items-center gap-2">
                          {selectedUserProfile.username}
                          {(selectedUserProfile.is_verified || selectedUserProfile.verified) && (
                            <BadgeCheck className="w-5 h-5 text-primary" />
                          )}
                        </h2>
                        <p className="text-sm text-muted-foreground">{selectedUserProfile.email}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={closeUserProfile} className="rounded-xl">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>

                  {/* Profile Content */}
                  <div className="p-4 sm:p-6 space-y-6">
                    {/* User Info Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-4 rounded-xl bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">{t('admin', 'role') || 'Роль'}</p>
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs border ${ROLE_CONFIG[selectedUserProfile.role]?.color}`}>
                          {(() => {
                            const RIcon = ROLE_CONFIG[selectedUserProfile.role]?.icon || Users;
                            return <RIcon className="w-3.5 h-3.5" />;
                          })()}
                          {t('admin', ROLE_CONFIG[selectedUserProfile.role]?.labelKey)}
                        </div>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">{t('admin', 'plan') || 'План'}</p>
                        <span className={`inline-block px-2 py-1 rounded-lg text-xs ${PLAN_CONFIG[selectedUserProfile.plan]?.color}`}>
                          {PLAN_CONFIG[selectedUserProfile.plan]?.label}
                        </span>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">{t('admin', 'pagesCount') || 'Страниц'}</p>
                        <p className="text-lg font-bold">{selectedUserProfile.page_count || 0}</p>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/50 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">{t('admin', 'totalClicks') || 'Всего кликов'}</p>
                        <p className="text-lg font-bold">{selectedUserProfile.total_clicks || 0}</p>
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-muted/30 border border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <Calendar className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">{t('admin', 'registrationDate') || 'Дата регистрации'}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {selectedUserProfile.created_at ? new Date(selectedUserProfile.created_at).toLocaleDateString('ru-RU', {
                            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          }) : '—'}
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-muted/30 border border-border">
                        <div className="flex items-center gap-2 mb-3">
                          <Shield className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">{t('admin', 'status') || 'Статус'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedUserProfile.is_banned ? (
                            <span className="px-2 py-1 rounded-lg text-xs bg-red-500/20 text-red-400 border border-red-500/30">
                              {t('admin', 'banned') || 'Заблокирован'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded-lg text-xs bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                              {t('admin', 'active') || 'Активен'}
                            </span>
                          )}
                          {(selectedUserProfile.is_verified || selectedUserProfile.verified) && (
                            <span className="px-2 py-1 rounded-lg text-xs bg-primary/20 text-primary border border-primary/30">
                              {t('admin', 'verifiedBadge') || 'Верифицирован'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* User Pages Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <FileText className="w-5 h-5 text-primary" />
                        <h3 className="text-lg font-semibold">
                          {t('admin', 'userPages') || 'Страницы пользователя'} ({selectedUserPages.length})
                        </h3>
                      </div>

                      {selectedUserPages.length > 0 ? (
                        <div className="space-y-3">
                          {selectedUserPages.map((page, i) => (
                            <motion.div
                              key={page.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05 }}
                              className="p-4 rounded-xl bg-muted/30 border border-border hover:border-primary/30 transition-all"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                {/* Cover & Info */}
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <div className="w-14 h-14 rounded-xl bg-muted overflow-hidden flex-shrink-0">
                                    {page.cover_image ? (
                                      <img 
                                        src={page.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${page.cover_image}` : page.cover_image}
                                        alt={page.title} 
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Music className="w-6 h-6 text-zinc-600" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="font-semibold truncate">{page.title || 'Без названия'}</p>
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                                        page.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                                      }`}>
                                        {page.status === 'active' ? t('admin', 'active') || 'Активна' : t('admin', 'disabled') || 'Отключена'}
                                      </span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">/{page.slug}</p>
                                  </div>
                                </div>

                                {/* Stats */}
                                <div className="flex items-center gap-4 pl-[68px] sm:pl-0">
                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="flex items-center gap-1 text-muted-foreground" title={t('admin', 'totalClicks') || 'Всего кликов'}>
                                      <MousePointer className="w-4 h-4" /> {page.total_clicks || 0}
                                    </span>
                                    <span className="flex items-center gap-1 text-muted-foreground" title={t('admin', 'clicks7d') || 'За 7 дней'}>
                                      <TrendingUp className="w-4 h-4" /> {page.clicks_7d || 0}
                                    </span>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-2">
                                    <a href={getPublicPageUrl(page.slug)} target="_blank" rel="noopener noreferrer">
                                      <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" title={t('admin', 'viewPublicPage') || 'Открыть страницу'}>
                                        <ExternalLink className="w-4 h-4" />
                                      </Button>
                                    </a>
                                    <a href={`/multilinks/${page.id}/edit`} target="_blank" rel="noopener noreferrer">
                                      <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" title={t('admin', 'editPage') || 'Редактировать'}>
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                    </a>
                                  </div>
                                </div>
                              </div>

                              {/* Page Created Date */}
                              <div className="mt-2 pt-2 border-t border-border/50 text-xs text-muted-foreground pl-[68px] sm:pl-0">
                                {t('admin', 'created') || 'Создана'}: {page.created_at ? new Date(page.created_at).toLocaleDateString('ru-RU') : '—'}
                                {page.updated_at && (
                                  <span className="ml-3">
                                    {t('admin', 'updated') || 'Обновлена'}: {new Date(page.updated_at).toLocaleDateString('ru-RU')}
                                  </span>
                                )}
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12 rounded-xl bg-muted/20 border border-dashed border-border">
                          <FileText className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
                          <p className="text-muted-foreground">{t('admin', 'noPages') || 'Пользователь ещё не создал страниц'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Custom confirm dialog (replaces native confirm) */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />

      {/* User actions modal: Ban / Delete */}
      <AnimatePresence>
        {userActions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setUserActions(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              className="w-full max-w-sm rounded-2xl bg-card border border-border shadow-2xl p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold">{t('admin', 'userActions') || 'Действия с пользователем'}</h3>
                <button onClick={() => setUserActions(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-sm text-muted-foreground mb-4 truncate">{userActions.username || userActions.email}</p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => { const u = userActions; setUserActions(null); toggleUserBan(u.id, u.is_banned || u.status === 'blocked'); }}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {(userActions.is_banned || userActions.status === 'blocked') ? (t('admin', 'userUnbanned') || 'Разбанить') : (t('admin', 'ban') || 'Забанить')}
                </Button>
                <Button
                  className="w-full justify-start bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => deleteUser(userActions)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('admin', 'deleteUser') || 'Удалить пользователя'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Sidebar>
  );
}
